import {
  isSpecialistLocationGatewayIdentifier,
  isSpecialistLocationGatewayRecordFresh,
  validateSpecialistLocationGatewayIntentRecord,
  validateSpecialistLocationGatewayPublication,
  type SpecialistLocationGatewayIntentRecordV1,
  type SpecialistLocationGatewayPublicationV1,
} from "./contract";

export interface SpecialistLocationGatewayProviderIdentity {
  providerId: string;
  providerRevision: string;
}

export interface SpecialistLocationGatewayProviderV1 {
  getIdentity(): SpecialistLocationGatewayProviderIdentity | null;
  isCurrentSource(intent: SpecialistLocationGatewayIntentRecordV1): boolean;
  resolve(
    intent: SpecialistLocationGatewayIntentRecordV1,
    context: { signal: AbortSignal },
  ): Promise<unknown>;
}

export type ResolveSpecialistLocationGatewayResult =
  | {
      status: "ready";
      publication: Readonly<SpecialistLocationGatewayPublicationV1>;
    }
  | {
      status: "unavailable";
      reason:
        | "aborted"
        | "intent-expired"
        | "invalid-intent"
        | "invalid-publication"
        | "provider-drift"
        | "provider-threw"
        | "source-drift";
    };

function sameIdentity(
  left: SpecialistLocationGatewayProviderIdentity | null,
  right: SpecialistLocationGatewayProviderIdentity | null,
) {
  return (
    left !== null &&
    right !== null &&
    left.providerId === right.providerId &&
    left.providerRevision === right.providerRevision
  );
}

function snapshotIdentity(
  value: SpecialistLocationGatewayProviderIdentity | null,
): SpecialistLocationGatewayProviderIdentity | null {
  return value &&
    isSpecialistLocationGatewayIdentifier(value.providerId) &&
    isSpecialistLocationGatewayIdentifier(value.providerRevision)
    ? {
        providerId: value.providerId,
        providerRevision: value.providerRevision,
      }
    : null;
}

function deepFreeze<T>(value: T): Readonly<T> {
  if (value && typeof value === "object" && !Object.isFrozen(value)) {
    Object.freeze(value);
    for (const child of Object.values(value as Record<string, unknown>)) {
      deepFreeze(child);
    }
  }
  return value;
}

function checkProviderFrontier(input: {
  provider: SpecialistLocationGatewayProviderV1;
  intent: Readonly<SpecialistLocationGatewayIntentRecordV1>;
  expectedIdentity: SpecialistLocationGatewayProviderIdentity;
  signal: AbortSignal;
  isCurrentHandoff: (
    intent: Readonly<SpecialistLocationGatewayIntentRecordV1>,
  ) => boolean;
}):
  | { ok: true }
  | {
      ok: false;
      reason: "aborted" | "provider-drift" | "provider-threw" | "source-drift";
    } {
  if (input.signal.aborted) return { ok: false, reason: "aborted" };
  let identity: SpecialistLocationGatewayProviderIdentity | null;
  try {
    identity = snapshotIdentity(input.provider.getIdentity());
  } catch {
    return { ok: false, reason: "provider-threw" };
  }
  if (input.signal.aborted) return { ok: false, reason: "aborted" };
  if (!sameIdentity(input.expectedIdentity, identity)) {
    return { ok: false, reason: "provider-drift" };
  }
  let sourceIsCurrent: boolean;
  try {
    sourceIsCurrent = input.provider.isCurrentSource(input.intent) === true;
  } catch {
    return { ok: false, reason: "provider-threw" };
  }
  if (input.signal.aborted) return { ok: false, reason: "aborted" };
  if (!sourceIsCurrent) return { ok: false, reason: "source-drift" };
  let handoffIsCurrent: boolean;
  try {
    handoffIsCurrent = input.isCurrentHandoff(input.intent) === true;
  } catch {
    return { ok: false, reason: "provider-threw" };
  }
  if (input.signal.aborted) return { ok: false, reason: "aborted" };
  return handoffIsCurrent
    ? { ok: true }
    : { ok: false, reason: "source-drift" };
}

/**
 * Async resolver with a before/after provider identity snapshot. A late
 * publication cannot become ready after abort, source drift, generation drift,
 * provider replacement, revision change, or expiry.
 */
export async function resolveSpecialistLocationGateway(input: {
  intent: SpecialistLocationGatewayIntentRecordV1;
  provider: SpecialistLocationGatewayProviderV1;
  signal: AbortSignal;
  now?: () => number;
  isCurrentHandoff: (
    intent: Readonly<SpecialistLocationGatewayIntentRecordV1>,
  ) => boolean;
}): Promise<ResolveSpecialistLocationGatewayResult> {
  const now = input.now ?? Date.now;
  const intentValidation = validateSpecialistLocationGatewayIntentRecord(
    input.intent,
  );
  if (!intentValidation.valid) {
    return { status: "unavailable", reason: "invalid-intent" };
  }
  const canonicalIntent = JSON.stringify(intentValidation.draft);
  const frozenIntent = deepFreeze(intentValidation.draft);
  let startedAtEpochMs: number;
  try {
    startedAtEpochMs = now();
  } catch {
    return { status: "unavailable", reason: "provider-threw" };
  }
  if (!isSpecialistLocationGatewayRecordFresh(frozenIntent, startedAtEpochMs)) {
    return { status: "unavailable", reason: "intent-expired" };
  }
  if (input.signal.aborted) {
    return { status: "unavailable", reason: "aborted" };
  }

  let identityBefore: SpecialistLocationGatewayProviderIdentity | null;
  try {
    identityBefore = snapshotIdentity(input.provider.getIdentity());
  } catch {
    return { status: "unavailable", reason: "provider-threw" };
  }
  if (input.signal.aborted) {
    return { status: "unavailable", reason: "aborted" };
  }
  if (
    !identityBefore ||
    identityBefore.providerId !== frozenIntent.gatewayProviderId ||
    identityBefore.providerRevision !== frozenIntent.gatewayProviderRevision
  ) {
    return { status: "unavailable", reason: "provider-drift" };
  }
  const initialFrontier = checkProviderFrontier({
    ...input,
    intent: frozenIntent,
    expectedIdentity: identityBefore,
  });
  if (!initialFrontier.ok) {
    return { status: "unavailable", reason: initialFrontier.reason };
  }
  if (input.signal.aborted) {
    return { status: "unavailable", reason: "aborted" };
  }

  let candidate: unknown;
  let resolveProvider: SpecialistLocationGatewayProviderV1["resolve"];
  try {
    resolveProvider = input.provider.resolve;
  } catch {
    return { status: "unavailable", reason: "provider-threw" };
  }
  if (input.signal.aborted) {
    return { status: "unavailable", reason: "aborted" };
  }
  try {
    candidate = await resolveProvider.call(input.provider, frozenIntent, {
      signal: input.signal,
    });
  } catch {
    return {
      status: "unavailable",
      reason: input.signal.aborted ? "aborted" : "provider-threw",
    };
  }
  if (input.signal.aborted) {
    return { status: "unavailable", reason: "aborted" };
  }

  if (JSON.stringify(frozenIntent) !== canonicalIntent) {
    return { status: "unavailable", reason: "source-drift" };
  }
  const postResolveFrontier = checkProviderFrontier({
    ...input,
    intent: frozenIntent,
    expectedIdentity: identityBefore,
  });
  if (!postResolveFrontier.ok) {
    return { status: "unavailable", reason: postResolveFrontier.reason };
  }

  let completedAtEpochMs: number;
  try {
    completedAtEpochMs = now();
  } catch {
    return { status: "unavailable", reason: "provider-threw" };
  }
  if (completedAtEpochMs < startedAtEpochMs) {
    return { status: "unavailable", reason: "provider-drift" };
  }
  if (input.signal.aborted) {
    return { status: "unavailable", reason: "aborted" };
  }
  const preValidationFrontier = checkProviderFrontier({
    ...input,
    intent: frozenIntent,
    expectedIdentity: identityBefore,
  });
  if (!preValidationFrontier.ok) {
    return { status: "unavailable", reason: preValidationFrontier.reason };
  }
  let validated: ReturnType<
    typeof validateSpecialistLocationGatewayPublication
  >;
  try {
    validated = validateSpecialistLocationGatewayPublication(candidate, {
      intentRecord: intentValidation.draft,
      nowEpochMs: completedAtEpochMs,
      expectedProviderId: identityBefore.providerId,
      expectedProviderRevision: identityBefore.providerRevision,
    });
  } catch {
    return { status: "unavailable", reason: "invalid-publication" };
  }
  if (input.signal.aborted) {
    return { status: "unavailable", reason: "aborted" };
  }
  const postValidationFrontier = checkProviderFrontier({
    ...input,
    intent: frozenIntent,
    expectedIdentity: identityBefore,
  });
  if (!postValidationFrontier.ok) {
    return { status: "unavailable", reason: postValidationFrontier.reason };
  }
  if (input.signal.aborted) {
    return { status: "unavailable", reason: "aborted" };
  }
  return validated.valid
    ? {
        status: "ready",
        publication: deepFreeze(validated.publication),
      }
    : { status: "unavailable", reason: "invalid-publication" };
}
